import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { History } from 'history';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import last from 'lodash/last';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import { useAnalytics } from '@modernhealth/web-analytics';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import {
    AccountProviderSchedulingType,
    CareRecommendationRecommendation,
    CoachNode,
    CostEstimation,
} from 'src/generated/webapp_gql';
import { useGetProviderAvailabilityQuery } from 'src/modules/providers/documents/getProviderAvailability.generated';
import { useRemoveProviderMutation } from 'src/modules/providers/documents/removeProviderMutation.generated';
import { ProviderAvailability } from 'src/modules/providers/models/providers';
import {
    getMyProviderAssignments,
    getProviderByUuid,
    getMyCoaches,
    getMyTherapists,
    getConversationByProviderUuid,
} from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType, Role } from 'src/modules/user/models/user';
import {
    getUserInformation,
    getMostRecentCareRecommendation,
} from 'src/modules/user/selectors/userSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { generateDatesToDisplayOnAvailabilityCalendar } from 'src/shared/helpers/generateDatesToDisplayOnAvailabilityCalendar';
import { color, font, layout } from 'src/shared/styles/variables';

import { CoachMissingAvailabilityInfo } from '../components/CoachMissingAvailabilityInfo';
import { ScheduleCalendar } from '../components/ScheduleCalendar';
import { ScheduleSessionConfirmationModal } from '../components/ScheduleSessionConfirmationModal';
import { ScheduleHealthPlanSessionConfirmationModal } from '../components/ScheduleHealthPlanSessionConfirmationModal';
import {
    Calendar,
    getCalendarsForSession,
    openCalendarForSession,
} from '../components/AddToCalendarModal';
import { useCreateCoachingSessionMutation } from '../documents/createCoachingSessionMutation.generated';
import { useRescheduleCoachingSessionMutation } from '../documents/rescheduleCoachingSessionMutation.generated';
import { SessionSlot, StringifiedSessionSlot, VisitState } from '../models/videoSessions';
import { getSessionUtilizationInfo, getVideoSessions } from '../selectors/videoSessionsSelectors';

const OutsideWrapper = styled.div({
    maxWidth: layout.containerWidth,
    position: 'relative',
    width: '100%',
    margin: '0 auto',
});

const Wrapper = styled.div({
    maxWidth: 979,
    width: '100%',
    margin: '50px auto 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const Title = styled.h3({
    color: color.pickledBluewood,
    marginBottom: 15,
    ...font.black(font.size.extraLarge),
});

const Subtitle = styled.h5({
    color: color.steelGray,
    textAlign: 'center',
    width: '70%',
    ...font.roman(font.size.medium),
});

export interface ScheduleSessionCalendarPageLocationState {
    coachUuid?: string;
    sessionToRescheduleUuid?: string;
    isFirstSession?: boolean;
    removeCoachAssignmentOnBackPress?: boolean;
    disableBack?: boolean;
    onboarding?: boolean;
    providerMatchAttemptUuid?: string;
}

type ProxyProps = RouteComponentProps<{}, never, ScheduleSessionCalendarPageLocationState>;

const Proxy: React.FC<ProxyProps> = ({ location, history }) => {
    const coachUuid = get(location, 'state.coachUuid');
    const disableBack = get(location, 'state.disableBack');
    const sessionToRescheduleUuid = get(location, 'state.sessionToRescheduleUuid');
    const isFirstSession = get(location, 'state.isFirstSession');
    const onboardingMode = get(location, 'state.onboarding');
    const providerMatchAttemptUuid = location.state?.providerMatchAttemptUuid;
    const provider = useSelector(getProviderByUuid(coachUuid));
    const removeCoachAssignmentOnBackPress = get(
        location,
        'state.removeCoachAssignmentOnBackPress',
    );
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const coaches = useSelector(getMyCoaches);
    const therapists = useSelector(getMyTherapists);

    const dispatch = useDispatch<Dispatch>();

    useEffect(() => {
        dispatch.providers.fetchMyProviders();
        dispatch.user.fetchUserInfo();

        if (!coachUuid) {
            const recommendation = careRecommendation?.recommendation;
            let redirectionLink = '/schedule-session/select-coach';

            if (recommendation) {
                switch (recommendation) {
                    case CareRecommendationRecommendation.Therapy:
                        if (!therapists.length) {
                            redirectionLink = '/work-with-a-therapist';
                        }
                        break;
                    case CareRecommendationRecommendation.MentalHealthCoaching:
                        if (!coaches.length) {
                            redirectionLink = '/work-with-a-mental-health-coach';
                        }
                        break;
                    case CareRecommendationRecommendation.Coaching:
                        if (!coaches.length) {
                            redirectionLink = '/work-with-a-coach';
                        }
                        break;
                    default:
                        redirectionLink = '/schedule-session/select-coach';
                }
            }
            history.replace(redirectionLink);
        }
    }, []);

    useEffect(() => {
        if (provider?.uuid && !provider?.defaultVisitDuration) {
            Sentry.withScope(scope => {
                scope.setExtra('provider_uuid', provider.uuid);
                scope.setExtra('provider_object', provider);

                Sentry.captureException(
                    new Error(
                        "Couldn't retrieve default visit duration from the provider object, using default value",
                    ),
                );
            });
        }
    }, [provider]);

    if (!coachUuid) {
        return null;
    }

    if (!provider?.uuid) {
        return <CenteredSpinner />;
    }

    return (
        <ScheduleSessionCalendarPage
            isFirstSession={isFirstSession}
            provider={provider}
            onboardingMode={onboardingMode}
            disableBack={disableBack}
            sessionToRescheduleUuid={sessionToRescheduleUuid}
            history={history}
            removeCoachAssignmentOnBackPress={removeCoachAssignmentOnBackPress}
            providerMatchAttemptUuid={providerMatchAttemptUuid}
        />
    );
};

interface ScheduleSessionCalendarProps {
    provider: CoachNode;
    history: History;
    sessionToRescheduleUuid?: string;
    readonly?: boolean;
    isFirstSession?: boolean;
    onboardingMode?: boolean;
    disableBack?: boolean;
    removeCoachAssignmentOnBackPress?: boolean;
    providerMatchAttemptUuid?: string;
}

export const ScheduleSessionCalendarPage: React.FC<ScheduleSessionCalendarProps> = ({
    provider,
    history,
    sessionToRescheduleUuid,
    readonly,
    isFirstSession,
    onboardingMode = false,
    removeCoachAssignmentOnBackPress,
    disableBack,
    providerMatchAttemptUuid = '',
}) => {
    const { t } = useTranslation();
    const { hideSessionSchedulingWeb, showIntakeForm, pammMemberScheduling, pammCostEstimation } =
        useFlags();

    const sessionUtilizationInfo = useSelector(getSessionUtilizationInfo);
    const analytics = useAnalytics();
    const dispatch = useDispatch<Dispatch>();

    const [pendingSessionSlot, setPendingSessionSlot] = useState<SessionSlot | null>(null);
    const [sessionBookedUuid, setSessionBookedUuid] = useState<string | null>(null);
    const [lastSessionTaken, setLassSessionTaken] = useState<boolean>(false);
    const coachAssignmentUuid =
        useSelector(getMyProviderAssignments).find(({ coach }) => coach?.uuid === provider.uuid)
            ?.uuid || '';
    const isRoster = provider?.providerSchedulingType === AccountProviderSchedulingType.Roster;
    const user = useSelector(getUserInformation);
    const conversation = useSelector(getConversationByProviderUuid(provider.uuid));
    const sessions = useSelector(getVideoSessions);
    const session = sessions.find(s => s.uuid === sessionBookedUuid);

    const [unmatchingFinished, setUnmatchingFinished] = useState(false);
    const [isFetchingStoreData, setIsFetchingStoreData] = useState<boolean>(false);
    const [isModalTypeHealthPlan, setIsModalTypeHealthPlan] = useState<boolean>(false);

    const [removeProvider, { loading: unmatchingInProgress }] = useRemoveProviderMutation({
        client: appClient,
    });

    const isMemberHPIEligible =
        provider.providerType === ProviderType.THERAPIST &&
        sessionUtilizationInfo.therapyVisitsRemaining <= 0 &&
        user.hpiEligibilityStatus;

    const isPsychiatrist = provider?.providerType === ProviderType.PSYCHIATRIST;
    const isPammSchedulingEnabled =
        !isPsychiatrist || (pammMemberScheduling && user.isCompanyPsychiatryEnabled);

    useEffect(() => {
        if (!isPammSchedulingEnabled) navigationService.navigate('/');
    }, [isPammSchedulingEnabled]);

    useEffect(() => {
        const unblock = history.block((_location, action) => {
            // block navigate back attempt and go back manually
            if (action === 'POP') {
                if (!removeCoachAssignmentOnBackPress) {
                    return;
                } else {
                    if (!unmatchingInProgress && !unmatchingFinished) {
                        handleGoBack();
                        return false;
                    } else if (unmatchingInProgress && !unmatchingFinished) {
                        return false;
                    } else if (!unmatchingInProgress && unmatchingFinished) {
                        return;
                    } else {
                        return false;
                    }
                }
            } else {
                return;
            }
        });

        return () => {
            unblock();
        };
    }, [unmatchingInProgress, unmatchingFinished]);

    useEffect(() => {
        if (!readonly && provider?.providerType) {
            const sessionsLeft =
                provider.providerType === ProviderType.COACH
                    ? sessionUtilizationInfo.coachingVisitsRemaining
                    : sessionUtilizationInfo.therapyVisitsRemaining;

            const hasHPIMemberCompletedCheck =
                !sessionToRescheduleUuid &&
                provider.providerType === ProviderType.THERAPIST &&
                user.isCompanyHpiEnabled &&
                user.hpiMemberInsurance === null;

            const qualifiesForHPISessions: boolean = user.isCompanyHpiEnabled && sessionsLeft === 0;

            if (
                provider.providerType === ProviderType.THERAPIST &&
                qualifiesForHPISessions &&
                !user.hpiEligibilityStatus
            ) {
                return history.push('/extend-therapy-sessions');
            }

            if (
                sessionsLeft === 0 &&
                !isMemberHPIEligible &&
                !hasHPIMemberCompletedCheck &&
                !lastSessionTaken &&
                provider?.providerType !== ProviderType.PSYCHIATRIST
            ) {
                toastNotificationsService.renderWarningToast(
                    t(
                        `videoSessions:no_more_sessions.${
                            provider?.providerType === ProviderType.COACH ? 'coaching' : 'therapy'
                        }`,
                    ),
                );
                history.push('/');
            }
        }
    }, [readonly, provider, sessionUtilizationInfo, lastSessionTaken]);

    if (provider?.uuid === user.uuid && user.role === Role.MEMBER) {
        Sentry.withScope(scope => {
            scope.setExtras({
                user_uuid: user.uuid,
                provider_uuid: provider?.uuid,
            });
        });
        Sentry.captureException(
            new Error(
                'CC-1894: Member attempted to query for own session. See breadcrumbs to investigate how they got into this state.',
            ),
        );
    }

    const {
        data,
        loading: availabilityLoading,
        refetch,
    } = useGetProviderAvailabilityQuery({
        variables: { providerUuid: provider.uuid },
        client: appClient,
        fetchPolicy: 'network-only',
        skip: isRoster,
    });
    const [createCoachingSession, { loading: createSessionLoading }] =
        useCreateCoachingSessionMutation({
            client: appClient,
        });
    const [rescheduleCoachingSession, { loading: rescheduleSessionLoading }] =
        useRescheduleCoachingSessionMutation({
            client: appClient,
        });

    const loading = createSessionLoading || rescheduleSessionLoading || isFetchingStoreData;

    useEffect(() => {
        if (!readonly && hideSessionSchedulingWeb) {
            history.replace('/dashboard');
        }
    }, [hideSessionSchedulingWeb, readonly]);

    const handleGoBack = async () => {
        if (removeCoachAssignmentOnBackPress) {
            if (!unmatchingInProgress) {
                dispatch.providers.removeMyProvider(provider.uuid);

                await removeProvider({ variables: { providerUuid: provider.uuid } }).then(() => {
                    dispatch.providers.fetchMyProviders();
                });
                setUnmatchingFinished(true);
                history.goBack();
            }
        }
    };

    const handleBackButtonClick = () => {
        analytics.trackEvent(
            EventLexicon.clickScheduleSessionCalendarBookASession({
                providerType: provider.providerType ?? '',
                providerSubtype: provider.psychiatristSubtype ?? '',
                providerMatchAttemptUuid,
                providerAssignmentUuid: coachAssignmentUuid,
                providerUuid: provider.uuid,
                destination: 'back',
            }),
        );

        // because we are listening for 'POP' action
        // the handleGoBack function will be called after we try to simply goBack
        history.goBack();
    };

    const onSkipScheduling = () => {
        analytics.trackEvent(
            EventLexicon.clickScheduleSessionCalendarBookASession({
                providerType: provider.providerType ?? '',
                providerSubtype: provider.psychiatristSubtype ?? '',
                providerMatchAttemptUuid,
                providerAssignmentUuid: coachAssignmentUuid,
                providerUuid: provider.uuid,
                destination: 'cancel',
            }),
        );

        // ask a user to fill out the care form if this user never had a session scheduled with this coach before
        if (provider?.providerType === ProviderType.COACH && showIntakeForm) {
            const amountOfSessionsWithThisCoach = sessions.filter(
                session => session.coachAssignment?.coach?.uuid === provider.uuid,
            ).length;

            const amountOfSessionsForIntakeForm = sessionBookedUuid ? 1 : 0;

            if (
                amountOfSessionsWithThisCoach === amountOfSessionsForIntakeForm &&
                coachAssignmentUuid
            ) {
                return navigationService.navigate(`/care-form/${coachAssignmentUuid}`);
            }
        }

        return goHome();
    };

    const goHome = () => navigationService.navigate('/dashboard');

    const handleAddToCalendar = (calendar: Calendar) => {
        if (!session) {
            return;
        }

        openCalendarForSession(session, calendar);
    };

    const handleConfirmationModalExit = () => {
        if (loading) {
            return;
        }

        setPendingSessionSlot(null);

        if (sessionBookedUuid) {
            setSessionBookedUuid(null);

            onSkipScheduling();
        }
    };

    const handleConfirmationModalClose = () => {
        analytics.trackEvent(
            EventLexicon.clickScheduleSessionCalendarConfirmSession({
                providerType: provider.providerType ?? '',
                providerSubtype: provider.psychiatristSubtype ?? '',
                providerMatchAttemptUuid,
                providerAssignmentUuid: coachAssignmentUuid,
                providerUuid: provider.uuid,
                destination: 'cancel',
            }),
        );

        if (loading) {
            return;
        }

        if (!!refetch) {
            refetch();
        }

        setPendingSessionSlot(null);

        if (sessionBookedUuid) {
            setSessionBookedUuid(null);
        }

        if (isPsychiatrist) {
            history.push('/dashboard', { disableBack: true });
        }
    };

    // Make sure HPI info is up to date before full render
    if (!provider) {
        return <CenteredSpinner />;
    }

    if (isRoster) {
        const path = conversation ? `/messages/${conversation.uuid}` : '/messages';

        return <Redirect to={path} />;
    }

    const providerAvailability = get(data, 'providerAvailability') || [];
    const hasCalendarSetup = get(data, 'hasCalendarSetup') || false;

    const dates = generateDatesToDisplayOnAvailabilityCalendar(last(providerAvailability)?.end);

    if (!availabilityLoading && providerAvailability.length === 0) {
        return (
            <CoachMissingAvailabilityInfo
                onCtaClick={isRoster ? goHome : handleBackButtonClick}
                hasCalendarSetup={hasCalendarSetup}
                isFirstSession={isFirstSession}
                isRoster={isRoster}
            />
        );
    }
    const sessionSlots: SessionSlot[] = providerAvailability.map(
        ({ start, end }: ProviderAvailability) => ({
            start: moment.utc(start).local(),
            end: moment.utc(end).local(),
        }),
    );

    const visitDurationInMinutes = moment(providerAvailability[0]?.end).diff(
        moment(providerAvailability[0]?.start),
        'minutes',
    );

    const datesWithSessionSlots = dates.map(date => {
        const slots = sessionSlots.filter(({ start }) => start.isSame(date, 'day'));

        return {
            date,
            slots,
        };
    });

    const showConfirmationModal = (sessionSlot: SessionSlot) => {
        analytics.trackEvent(
            EventLexicon.clickScheduleSessionCalendarBookASession({
                providerType: provider.providerType ?? '',
                providerSubtype: provider.psychiatristSubtype ?? '',
                providerMatchAttemptUuid,
                providerAssignmentUuid: coachAssignmentUuid,
                providerUuid: provider.uuid,
                destination: 'confirm',
            }),
        );
        setPendingSessionSlot(sessionSlot);
        setIsModalTypeHealthPlan(isMemberHPIEligible || (isPsychiatrist && pammCostEstimation));
    };

    const handleSessionBookConfirm = async () => {
        analytics.trackEvent(
            EventLexicon.clickScheduleSessionCalendarConfirmSession({
                providerType: provider.providerType ?? '',
                providerSubtype: provider.psychiatristSubtype ?? '',
                providerMatchAttemptUuid,
                providerAssignmentUuid: coachAssignmentUuid,
                providerUuid: provider.uuid,
                destination: 'confirm',
            }),
        );

        if (!pendingSessionSlot || loading) {
            return;
        }

        const start = pendingSessionSlot.start.clone().utc();
        const end = pendingSessionSlot.end.clone().utc();

        try {
            let uuid: string | null = null;
            let costEstimation: CostEstimation | undefined | null;
            let visitState: VisitState | undefined;

            setIsFetchingStoreData(true);

            if (sessionToRescheduleUuid) {
                const { data } = await rescheduleCoachingSession({
                    variables: {
                        sessionUuid: sessionToRescheduleUuid,
                        start,
                    },
                });

                if (!data?.rescheduleEvent?.ok) {
                    throw new Error();
                }

                uuid = sessionToRescheduleUuid;
            } else {
                const { data } = await createCoachingSession({
                    variables: {
                        coachUuid: provider.uuid,
                        start,
                        end,
                    },
                });

                if (data?.createEvent?.formattedResponse?.__typename === 'ExpectedError') {
                    const errorMsg = data.createEvent.formattedResponse.message;
                    throw new Error(errorMsg);
                }

                if (data?.createEvent?.formattedResponse?.__typename === 'CreateEventResult') {
                    if (costEstimation?.error) {
                        setSessionBookedUuid(null);
                        setIsFetchingStoreData(false);

                        toastNotificationsService.renderErrorToast(costEstimation.error);
                        return;
                    }
                    uuid = data.createEvent.formattedResponse.uuid;
                    costEstimation = data.createEvent.formattedResponse?.costEstimation;
                    visitState = data.createEvent.formattedResponse.state as VisitState;
                    // used 'as', because property 'state' is typed as string in BE
                }
            }

            // Re-fetch sessions after new one was created
            await dispatch.videoSessions.fetchSessions();
            // Re-fetch user info since we're getting session utilization info from `me` query
            await dispatch.user.fetchUserInfo();

            setIsFetchingStoreData(false);

            switch (visitState) {
                case VisitState.PendingPayment:
                case VisitState.PendingConfirmation:
                    const sessionSlotStringified: StringifiedSessionSlot = {
                        start: pendingSessionSlot.start.utc().toString(),
                        end: pendingSessionSlot.end.utc().toString(),
                    };
                    history.push({
                        pathname: '/payments/session-review',
                        state: {
                            providerUuid: provider.uuid,
                            sessionSlot: sessionSlotStringified,
                            session: uuid,
                            onboardingMode,
                            costEstimation,
                        },
                    });
                    break;
                default:
                    if (get(user, 'visitsRemaining.therapyVisitsRemaining') <= 1) {
                        setLassSessionTaken(true);
                    }

                    if (
                        !sessionToRescheduleUuid &&
                        user.isCompanyHpiEnabled &&
                        provider.providerType === ProviderType.THERAPIST &&
                        get(user, 'visitsRemaining.therapyVisitsRemaining') <= 1 &&
                        user.hpiMemberInsurance === null
                    ) {
                        history.push({
                            pathname: '/extend-therapy-last-session',
                            state: {
                                sessionSlot: pendingSessionSlot,
                                provider: provider,
                                sessionBookedUuid: uuid,
                            },
                        });
                        return;
                    }

                    setSessionBookedUuid(uuid);
            }
        } catch (error) {
            setSessionBookedUuid(null);
            setIsFetchingStoreData(false);

            const message = get(error, 'message');

            const errorMessage = message
                ? message.replace('GraphQL error: ', '')
                : config.messages.genericError;

            toastNotificationsService.renderErrorToast(errorMessage);
        }
    };

    const getDescriptionText = () => {
        if (provider?.providerType === ProviderType.PSYCHIATRIST) {
            return visitDurationInMinutes === 60
                ? t('videoSessions:calendar_page.description.initial_evaluation')
                : t('videoSessions:calendar_page.description.follow_up');
        }
        return t('videoSessions:calendar_page.description', {
            visitDuration: visitDurationInMinutes,
            providerName: provider.firstName,
        });
    };

    return (
        <>
            <OutsideWrapper>
                <Wrapper>
                    <Title>{t('videoSessions:calendar_page.title')}</Title>

                    {availabilityLoading ? null : (
                        <>
                            <Subtitle>{getDescriptionText()}</Subtitle>
                            <ScheduleCalendar
                                loading={availabilityLoading}
                                datesWithSessionSlots={datesWithSessionSlots}
                                amountOfWeeks={Math.ceil(datesWithSessionSlots.length / 7)}
                                onBookSessionClick={showConfirmationModal}
                                readonly={readonly}
                                disableBack={disableBack}
                                onBack={handleBackButtonClick}
                                handleNotNowClick={onSkipScheduling}
                            />
                        </>
                    )}

                    {isModalTypeHealthPlan && pendingSessionSlot && (
                        <ScheduleHealthPlanSessionConfirmationModal
                            isVisible={!!pendingSessionSlot}
                            isLoading={loading}
                            sessionSlot={pendingSessionSlot}
                            onClose={handleConfirmationModalClose}
                            onPrimaryPress={handleSessionBookConfirm}
                            isPamm={isPsychiatrist}
                        />
                    )}

                    {!isModalTypeHealthPlan && pendingSessionSlot && provider && (
                        <ScheduleSessionConfirmationModal
                            visible={!!pendingSessionSlot}
                            sessionSlot={pendingSessionSlot}
                            onExit={handleConfirmationModalExit}
                            onConfirm={handleSessionBookConfirm}
                            onClose={handleConfirmationModalClose}
                            onSessionBookedConfirm={handleConfirmationModalClose}
                            onboardingMode={onboardingMode}
                            onSkip={onSkipScheduling}
                            onAddToCalendar={handleAddToCalendar}
                            calendars={getCalendarsForSession(session)}
                            loading={loading}
                            sessionBooked={!!sessionBookedUuid}
                            provider={provider}
                        />
                    )}
                </Wrapper>
            </OutsideWrapper>

            {availabilityLoading && <CenteredSpinner />}
        </>
    );
};

export default Proxy;
